<script setup lang="ts">
/**
 * Plain Vue rather than a Reka primitive: a button has no overlay, no focus
 * trap and no roving tabindex, so there is nothing hard to delegate.
 *
 * Copy arrives as a prop. A `libs/ui` component never calls `useI18n()` - it
 * has no idea which app it is in, and translating here would put user-facing
 * French and English inside a package that is supposed to be presentation only.
 */
withDefaults(
  defineProps<{
    label: string;
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
    size?: 'sm' | 'md';
    type?: 'button' | 'submit' | 'reset';
    disabled?: boolean;
    loading?: boolean;
    /** Renders the slot only; `label` becomes the accessible name. */
    iconOnly?: boolean;
    block?: boolean;
  }>(),
  {
    variant: 'secondary',
    size: 'md',
    type: 'button',
    disabled: false,
    loading: false,
    iconOnly: false,
    block: false,
  },
);

const emit = defineEmits<{ click: [MouseEvent] }>();

function onClick(event: MouseEvent): void {
  emit('click', event);
}
</script>

<template>
  <button
    :type="type"
    class="hb-button"
    :class="[`hb-button--${variant}`, `hb-button--${size}`, { 'hb-button--icon': iconOnly, 'hb-button--block': block }]"
    :disabled="disabled || loading"
    :aria-label="iconOnly ? label : undefined"
    :aria-busy="loading || undefined"
    @click="onClick"
  >
    <span v-if="loading" class="hb-button__spinner" aria-hidden="true" />
    <slot />
    <span v-if="!iconOnly" class="hb-button__label">{{ label }}</span>
  </button>
</template>

<style scoped>
.hb-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--hb-space-2);

  /* Touch first: a pointer-sized button is unusable on a phone, and this is
     the base rule because the CSS is mobile-first. */
  min-height: var(--hb-touch-target);
  padding: 0 var(--hb-space-4);
  border: 1px solid transparent;
  border-radius: var(--hb-radius-md);
  font-family: var(--hb-font-sans);
  font-size: var(--hb-text-sm);
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  transition:
    background-color var(--hb-duration-fast) var(--hb-ease),
    border-color var(--hb-duration-fast) var(--hb-ease);
}

.hb-button:focus-visible {
  outline: 2px solid var(--hb-focus-ring);
  outline-offset: 2px;
}

.hb-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.hb-button--block {
  width: 100%;
}

.hb-button--icon {
  min-width: var(--hb-touch-target);
  padding: 0;
}

.hb-button--sm {
  min-height: var(--hb-touch-target);
  padding: 0 var(--hb-space-3);
  font-size: var(--hb-text-xs);
}

/* Pointer devices can afford a denser control; touch devices cannot, so the
   smaller height is applied only where a fine pointer is present. */
@media (pointer: fine) {
  .hb-button--sm {
    min-height: 32px;
  }

  .hb-button--md {
    min-height: 38px;
  }

  .hb-button--icon {
    min-width: 38px;
  }
}

.hb-button--primary {
  background: var(--hb-accent);
  color: var(--hb-accent-contrast);
}

.hb-button--primary:hover:not(:disabled) {
  background: var(--hb-accent-hover);
}

.hb-button--secondary {
  background: var(--hb-surface-raised);
  border-color: var(--hb-border);
  color: var(--hb-text);
}

.hb-button--secondary:hover:not(:disabled) {
  border-color: var(--hb-border-strong);
}

.hb-button--ghost {
  background: transparent;
  color: var(--hb-text-muted);
}

.hb-button--ghost:hover:not(:disabled) {
  background: var(--hb-surface-raised);
  color: var(--hb-text);
}

.hb-button--danger {
  background: var(--hb-danger);
  color: var(--hb-danger-contrast);
}

.hb-button__spinner {
  width: 1em;
  height: 1em;
  border: 2px solid currentcolor;
  border-top-color: transparent;
  border-radius: var(--hb-radius-full);
  animation: hb-spin 600ms linear infinite;
}

@keyframes hb-spin {
  to {
    transform: rotate(1turn);
  }
}
</style>
